
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TableComponent from "../../Comman/TableComponent";
import { usePagination } from "../../../hooks/usePagination";
import { useSearch } from "../../../context/SearchContext";
import { ArticleService } from "../../../services/articleServic";
import { toast } from "react-toastify";
import { getStatusColor, STATUS_OPTIONS } from "../../../constants/Journalconstants";
import Pagination from "../../Comman/Pagination";
import { Eye, EyeOff } from "lucide-react";
import ProjectJournalList from "../../../Modal/Admin/projectJournalList";

const StatusBadge = ({ status }) => {
  const color = getStatusColor(status);
  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-lg text-xs whitespace-normal break-words font-medium ${color.bg} ${color.text} border border-[${color.bg}]`}
    >
      {status}
    </span>
  );
};

const filterInputClass =
  "h-10 px-3 rounded-md border border-[#d3cccc] bg-white text-sm outline-none focus:border-[#521785]";

export default function ClientArticlesJournals() {
  const location = useLocation();
  const { debounceSearchTerm } = useSearch();

  const { projectId, documentId } = location.state || {};

  const [journals, setJournals] = useState([]);
  const {currentPage, totalPages, goToPage, setTotalPages } = usePagination();
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showJournals, setShowJournals] = useState(false);
  const [sortOrder, setSortOrder] = useState("DESC");
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const navigate=useNavigate();

  const handleViewJournal = (journal) => {
    navigate(`/journal-details/${journal.id}`);
  };

  const columns = [
    {
      label: "Journal Name",
      headerClassName: "px-4 border-b border-[#d3cccc]",
      width: "15%",
      className: "px-4",
      render: (item) =>
        item?.journal?.journalLink ? (
          <a
            href={item?.journal?.journalLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="font-medium text-blue-700 hover:text-blue-900 hover:underline"
          >
            {item?.journal?.journalName}
          </a>
        ) : (
          item?.journal?.journalName
        ),
    },
    {
      label: "Title",
      field: "title",
      width: "24%",
      headerClassName: "px-4 border-b border-[#d3cccc]",
      className: "px-4",
      render: (item) => <p className="">{item?.journal?.document?.title ?? "-"}</p>,
    },
    {
      label: "Username",
      field: "username",
      width: "15%",
      headerClassName: "px-4 border-b border-[#d3cccc]",
      className: "px-4 whitespace-normal break-words",
    },
    {
      label: "Password",
      field: "password",
      width: "15%",
      headerClassName: "px-4 border-b border-[#d3cccc]",
      className: "px-4 whitespace-normal break-words",
      render: (item) => (
        <div className="flex items-center justify-center gap-2">
          <span className="break-all">
            {visiblePasswords[item.id]
              ? item.password || "-"
              : item.password
                ? "••••••••"
                : "-"}
          </span>

          {item.password && (
            <button
              type="button"
              onClick={() =>
                setVisiblePasswords((prev) => ({
                  ...prev,
                  [item.id]: !prev[item.id],
                }))
              }
              title={visiblePasswords[item.id] ? "Hide password" : "Show password"}
              aria-label={visiblePasswords[item.id] ? "Hide password" : "Show password"}
              className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-[#521785]"
            >
              {visiblePasswords[item.id] ? (
                <EyeOff size={15} className="text-gray-600"/>
              ) : (
                <Eye size={15} className="text-blue-600"/>
              )}
            </button>
          )}
        </div>
      ),
    },
    {
      label: "Status",
      width: "15%",
      headerClassName: "px-2 border-b border-[#d3cccc]",
      className: "px-2 whitespace-normal break-words",
      render: (item) => <StatusBadge status={item.status} />,
    },
    {
      label: "Last Updated",
      width: "10%",
      field: "updatedAt",
      headerClassName: "px-4 border-b border-[#d3cccc]",
      className: "px-4 whitespace-nowrap",
      render: (row) =>
        row.updatedAt
        ? new Date(row.updatedAt).toLocaleDateString("en-IN")
        : "-",
    },
    {
      label:"View",
      headerClassName: "px-4 border-b border-[#d3cccc]",
      className: "px-4 whitespace-nowrap",
      width: "6%",
      render: (row)=>(
        <div className="flex items-center justify-center">
              <button
                type="button"
                aria-label="View"
                title="View"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewJournal(row);
                }}
                className="p-1.5 rounded-md text-blue-600 hover:bg-[#f6eeee]"
              >
                <Eye/>
              </button>
            </div>
      ),
    }
  ];

  useEffect(() => {
    if (!documentId) {
      toast.error("Document ID is required.");
      setJournals([]);
      return;
    }

    fetchJournals();
  }, [
    currentPage,
    debounceSearchTerm,
    documentId,
    projectId,
    dateFilter,statusFilter,sortOrder,
    setTotalPages,
  ]);

  const fetchJournals = async () => {
      try {
        const response = await ArticleService.getJournalArticles(
          currentPage,
          10,
          debounceSearchTerm,
          documentId,
          projectId,
          dateFilter,
          statusFilter,
          sortOrder,
        );

        if (response) {
          setJournals(response?.data || []);
          setTotalPages(response?.pagination?.totalPages || 0);
        }
      } catch (error) {
        toast.error(error?.message || "Failed to fetch journal articles.");
      }
  };
    
  useEffect(() => {
    if (currentPage !== 1) {
      goToPage(1);
    }
  }, [debounceSearchTerm,dateFilter,statusFilter,sortOrder]);


  return (
    <div className="p-3">
      <div className="mb-2">
        <button
          type="button"
          onClick={()=>navigate(-1)}
          className="text-sm hover:underline mb-1"
        >
          &larr; Back to Articles
        </button>
        <p className="text-sm text-gray-500">
          {projectId}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="text-lg xl:text-xl font-semibold">Journals</h1>

        <div className="flex flex-wrap items-center gap-3">

          <input
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            aria-label="Filter by last updated date"
            className={filterInputClass}
          />

          <div className="flex items-center gap-2">      
            <select
              id="articleSortOrder"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                goToPage(1);
              }}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-[#521785] focus:ring-1 focus:ring-[#521785]"
            >
              <option value="DESC">Newest First</option>
              <option value="ASC">Oldest First</option>
            </select>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
            className={`${filterInputClass} w-full sm:w-56`}
          >
            <option value="">All Status</option>
            {STATUS_OPTIONS.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>

        </div>
      </div>

      <div className="space-y-3 mb-4 lg:hidden">
        {journals.length > 0 ? (
          journals.map((item, index) => (
            <div
              key={item.id ?? index}
              className="rounded-lg border border-[#d3cccc] bg-white p-4 shadow-sm"
            >
              {/* Journal name and status */}
              <div className="mb-3 flex flex-col items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                    Journal Name
                  </p>

                  {item?.journal?.journalLink ? (
                    <a
                      href={item.journal.journalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="break-words font-semibold text-blue-700 hover:underline"
                    >
                      {item?.journal?.journalName || "N/A"}
                    </a>
                  ) : (
                    <p className="break-words font-semibold text-gray-800">
                      {item?.journal?.journalName || "N/A"}
                    </p>
                  )}
                </div>

                <div className="shrink-0">
                  <StatusBadge status={item.status} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 border-t border-gray-100 pt-3 sm:grid-cols-2">
                {/* Title */}
                <div className="min-w-0 sm:col-span-2">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    Title
                  </p>
                  <p className="break-words text-sm text-gray-800">
                    {item?.journal?.document?.title || "-"}
                  </p>
                </div>

                {/* Username */}
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    Username
                  </p>
                  <p className="break-all text-sm text-gray-800">
                    {item.username || "-"}
                  </p>
                </div>

                {/* Password */}
                <div className="min-w-0">
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    Password
                  </p>
                  <p className="break-all text-sm text-gray-800">
                    {item.password || "-"}
                  </p>
                </div>

                {/* Last Updated */}
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-500">
                    Last Updated
                  </p>
                  <p className="text-sm text-gray-800">
                    {item.updatedAt
                      ? new Date(item.updatedAt).toLocaleDateString("en-IN")
                      : "-"}
                  </p>
                </div>

                {/* View action */}
                <div className="flex items-end justify-start sm:justify-end">
                  <button
                    type="button"
                    onClick={() => handleViewJournal(item)}
                    className="inline-flex items-center gap-2 rounded-md border border-[#d3cccc] px-3 py-2 text-sm font-medium text-blue-600 hover:bg-[#f6eeee]"
                  >
                    <Eye size={17} />
                    View Details
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-lg border border-[#d3cccc] bg-white p-6 text-center text-sm text-gray-500">
            No journals found.
          </div>
        )}
      </div>

      <div className="mb-4 hidden overflow-x-auto rounded-md border border-[#d3cccc] lg:block">
        <TableComponent
          columns={columns}
          data={journals}
          headerBg="bg-[#f6eeee]"
        />
      </div>
      
      {journals.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          goToPage={goToPage}
        />
      )}

      <ProjectJournalList
        projectId={projectId}
        documentId={documentId}
        isOpen={showJournals}
        onClose={() => setShowJournals(false)}
      />
    </div>
  );
}
